/**
 * LegalConsentModal - אישור המסמכים המשפטיים
 *
 * Shown immediately after the client signs the advisory agreement. Presents the
 * three lawyer-approved documents — מדיניות פרטיות, תקנון אתר ותנאי שימוש,
 * הצהרת נגישות — as links, and requires an explicit checkbox confirmation before
 * the client continues.
 *
 * The confirmation is recorded server-side (timestamp + IP + document revision)
 * via POST /agreement/legal-consent, so it stands as evidence of consent.
 *
 * Deliberately not dismissible: no backdrop-click close and no X — consent is a
 * gate, not a notice. Escape is likewise ignored (useDialogA11y is given a no-op
 * close handler) so the dialog cannot be skipped; focus is still trapped inside
 * it and returned on unmount, per WCAG 2.4.3.
 */

import { useRef, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useDialogA11y } from '../../hooks/useDialogA11y'

/** Must match LEGAL_DOCS_VERSION in the backend agreement controller. */
export const LEGAL_DOCS_VERSION = '2026-07'

const DOCUMENTS = [
  { to: '/privacy', label: 'מדיניות פרטיות' },
  { to: '/terms', label: 'תקנון אתר ותנאי שימוש' },
  { to: '/accessibility', label: 'הצהרת נגישות' },
] as const

interface LegalConsentModalProps {
  /** Called after the consent has been recorded successfully. */
  onConfirm: () => Promise<void> | void
  isSubmitting?: boolean
  error?: string
}

export default function LegalConsentModal({
  onConfirm,
  isSubmitting = false,
  error,
}: LegalConsentModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const [checked, setChecked] = useState(false)
  const [touched, setTouched] = useState(false)

  // Consent gate — Escape must not dismiss it.
  const noop = useCallback(() => {}, [])
  useDialogA11y(true, dialogRef, noop)

  const handleConfirm = () => {
    setTouched(true)
    if (!checked) return
    void onConfirm()
  }

  const showValidation = touched && !checked

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      dir="rtl"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-consent-title"
        aria-describedby="legal-consent-desc"
        tabIndex={-1}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 p-6 sm:p-8"
      >
        <h2 id="legal-consent-title" className="text-xl font-bold text-gray-900 mb-2">
          אישור המסמכים המשפטיים
        </h2>
        <p id="legal-consent-desc" className="text-sm text-gray-700 mb-4">
          ההסכם נחתם בהצלחה. לפני המשך השימוש בשירות, נבקשך לעיין במסמכים הבאים ולאשר את קריאתם.
          המסמכים נפתחים בלשונית חדשה, כך שהחלון הזה יישאר פתוח.
        </p>

        <ul className="list-disc pr-6 space-y-2 mb-5">
          {DOCUMENTS.map((doc) => (
            <li key={doc.to}>
              <Link
                to={doc.to}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-700 underline hover:text-primary-800 font-medium"
              >
                {doc.label}
              </Link>{' '}
              <span className="text-gray-400 text-xs">(נפתח בלשונית חדשה)</span>
            </li>
          ))}
        </ul>

        <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <input
            type="checkbox"
            id="legalDocsAccepted"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 w-4 h-4 cursor-pointer"
            aria-required="true"
            aria-invalid={showValidation}
            aria-describedby={showValidation ? 'legal-consent-error' : undefined}
          />
          <label htmlFor="legalDocsAccepted" className="text-sm text-gray-800 cursor-pointer">
            קראתי והבנתי את מדיניות הפרטיות, את תקנון האתר ותנאי השימוש ואת הצהרת הנגישות, ואני
            מסכים/ה לתנאיהם.
          </label>
        </div>

        {showValidation && (
          <p id="legal-consent-error" role="alert" className="text-red-600 text-sm mt-3">
            יש לסמן את תיבת האישור כדי להמשיך
          </p>
        )}
        {error && (
          <p role="alert" className="text-red-600 text-sm mt-3">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={isSubmitting}
          className="mt-6 w-full bg-blue-900 text-white py-3 rounded-lg font-semibold text-base hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? 'שולח...' : 'אני מאשר/ת והמשך'}
        </button>

        <p className="text-xs text-gray-400 text-center mt-3">
          האישור נשמר עם חותמת זמן וכתובת IP לצרכי תיעוד משפטי
        </p>
      </div>
    </div>
  )
}
